import { useTranslation } from 'react-i18next'
import { Card, CardBody } from '@structural-climbing/ui'
import { CompetitionAbout } from './CompetitionAbout'
import { formatWallClock, formatWallClockTime } from './wallClock'
import type { CompetitionDetailView } from './types'

interface Props {
  competition: CompetitionDetailView
}

/**
 * What a competitor sees between joining and the start.
 *
 * The scoreboard and the routes are deliberately not shown yet: before the
 * first attempt they would be an empty podium and a points table for routes
 * nobody can climb, which reads like something is broken rather than like the
 * competition simply has not started.
 */
export function CompetitionNotStarted({ competition }: Props) {
  const { t, i18n } = useTranslation()

  const mySession = competition.my_entry?.session_id
    ? competition.sessions.find((s) => s.id === competition.my_entry?.session_id)
    : null

  return (
    <div className="flex flex-col gap-3">
      <Card className="border border-success/30 bg-success/5 shadow-sm">
        <CardBody className="items-center gap-2 py-6 text-center">
          <span className="text-3xl" aria-hidden="true">
            ✅
          </span>
          <h2 className="text-lg font-bold">{t('competitions.joinedTitle')}</h2>
          <p className="text-sm text-foreground/70">
            {t('competitions.startsOn', {
              date: formatWallClock(competition.starts_at, i18n.language),
            })}
          </p>

          {mySession && (
            <p className="text-sm text-foreground/70">
              {t('competitions.yourGroup', {
                name: mySession.name,
                from: formatWallClockTime(mySession.starts_at),
                to: formatWallClockTime(mySession.ends_at),
              })}
            </p>
          )}

          <p className="mt-1 text-xs text-foreground/50">{t('competitions.notStartedNote')}</p>
        </CardBody>
      </Card>

      <CompetitionAbout description={competition.description} prizes={competition.prizes} />
    </div>
  )
}
